import ReconnectingWebSocket from 'reconnecting-websocket';
import { Event as WebSocketEvent } from 'reconnecting-websocket/events';
import { arrayFindBy } from 'utils/collection-manipulation/array-find-by';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { SwarmSuccessMessages } from 'utils/constants/swarm/swarm-success-messages';
import RidGenerator from 'utils/swarm/rid-generator';
import msgChecker from 'utils/swarm/swarm-msg-checker';
import {
  JackpotWebsocketEnhanced,
  RegisteredCommand
} from 'interfaces/spring-websocket-interfaces';
import Store from 'store';
import { setIsJackpotConnected } from 'store/actions';
import { setInternalJackpots } from 'store/actions/jackpot';

export const JACKPOT_CASINO_TYPE = 0;
export const JACKPOT_BETTING_TYPE = 5;

export const JackpotConnector = function (): Promise<JackpotWebsocketEnhanced> {
  return new Promise((resolve: (value: JackpotWebsocketEnhanced) => void) => {
    const partnerId = SpringConfigs.PARTNER_ID;
    const swarmUrl = SpringConfigs.JACKPOT_URL;

    const socket: JackpotWebsocketEnhanced = new ReconnectingWebSocket(
      swarmUrl,
      [],
      {
        reconnectionDelayGrowFactor: 1
      }
    ) as JackpotWebsocketEnhanced;

    let registeredCommands: RegisteredCommand[] = [];

    const _socketOpen = () => {
      if (socket.readyState !== ReconnectingWebSocket.OPEN) {
        return;
      } else {
        _requestSession();

        registeredCommands.forEach(
          ({ rid, callback, callbackUpdate, command }) => {
            sendCommand(command, rid, callback, callbackUpdate);
          }
        );
      }
    };

    const _socketClose = () => {
      Store.dispatch(setIsJackpotConnected(false));
      console.warn('WebSocket closed');
    };

    const _socketError = (e: WebSocketEvent) => {
      console.warn('ws Error :: ', e);
    };

    const _socketMessage = (msgEvent: MessageEvent): void => {
      const msg = JSON.parse(msgEvent.data);
      const { RequestId, Data: data } = msg;

      if (msgChecker.verifyRequestSession(msg)) {
        resolve(socket);

        Store.dispatch(setIsJackpotConnected(true));
        const jackpots = Store.getState().jackpot.internalJackpots;

        if (!jackpots) {
          Store.dispatch(setInternalJackpots({ casino: msg.Data || [] }));
        } else {
          const jackpotData = {
            casino: jackpots.casino,
            bet: msg.Data || []
          };

          Store.dispatch(setInternalJackpots(jackpotData));
        }

        return;
      }

      // process one time command
      if (msgChecker.verifyCommand(msg)) {
        const [commandObj, commandObjIndex] = arrayFindBy(
          registeredCommands,
          'rid',
          RequestId,
          true
        );

        if (commandObj) {
          registeredCommands.splice(commandObjIndex, 1);
          const { callback } = commandObj || {};

          if (typeof callback !== 'function') {
            return;
          }

          callback(data);
        }

        return;
      }

      if (msgChecker.verifySubscribe(msg)) {
        subscriptionData(msg.Data, msg.RequestId);

        return;
      }
    };

    /**
     * Processes first message after subscription and calls callback provided from component
     */
    const subscriptionData = (data: Object, requestId: string) => {
      const [command] = arrayFindBy(registeredCommands, 'rid', requestId, true);

      const { callback } = command || {};

      if (typeof callback !== 'function') {
        return;
      }

      callback(data);
    };

    const _requestSession = () => {
      if (SpringConfigs.MOCKED_DATA) {
        resolve(socket);
      } else {
        socket.send(
          JSON.stringify(_getPartnerJackpotsCommand(JACKPOT_CASINO_TYPE))
        );
        socket.send(
          JSON.stringify(_getPartnerJackpotsCommand(JACKPOT_BETTING_TYPE))
        );
      }
    };

    const _getPartnerJackpotsCommand = (type: number) => {
      const params = {
        SourceId: type,
        PartnerId: +partnerId
      };

      return {
        command: CommandNames.GET_JACKPOTS,
        Data: params,
        RequestId: RidGenerator.gForRequestSession()
      };
    };

    /**
     * Register command called from component
     * @param {String}   commandRid               rid generated in component for storing in registeredCommands
     * @param {Function} componentCallback        callback function provided by component that will be called after getting message
     * @param {Function} componentCallbackUpdate  callback function provided by component that will be called on data update message
     * @param commandObj
     */
    const registerCommand = (
      commandRid: string,
      componentCallback: Function | null,
      componentCallbackUpdate: Function | null,
      componentCallbackError: Function | null,
      commandObj: any,
      successMessage: SwarmSuccessMessages
    ) => {
      /**
       * for reconnecting purpose we check rid first and if we do not have it so
       * it means this subscription is not a old one and we should push it to registeredCommands
       */
      if (registeredCommands.findIndex(i => i.rid === commandRid) < 0) {
        registeredCommands.push({
          successMessage,
          rid: commandRid,
          callback: componentCallback,
          callbackUpdate: componentCallbackUpdate,
          callbackError: componentCallbackError,
          command: commandObj
        });
      }

      return true;
    };

    const unregisterCommand = (commandRid = '') => {
      if (commandRid) {
        const registeredCommandsCopy = [...registeredCommands];
        const commandIndex = registeredCommands.findIndex(
          i => i.rid === commandRid
        );

        if (commandIndex > -1) {
          registeredCommandsCopy.splice(commandIndex, 1);
          registeredCommands = registeredCommandsCopy;
        }
      }
    };

    /** Sends swarm command for component
     * @param {Object}   commandObj         actual command structure which should be sent to swarm
     * @param {String}   componentRid       rid generated in component for storing in registeredCommands
     * @param {Function} componentCallback  callback function provided by component that will be called after getting message
     * @param {Function} componentCallbackUpdate  callback function provided by component that will be called on data update message
     * @param {Function} componentCallbackError  callback function provided by component that will be called on swarm error message
     * */
    const sendCommand = (
      commandObj: any,
      componentRid: string | null = '',
      componentCallback: Function | null = null,
      componentCallbackUpdate: Function | null = null,
      componentCallbackError: Function | null = null,
      reConnect?: boolean
    ) => {
      if (commandObj.RequestId && commandObj.RequestId !== '') {
        componentRid = commandObj.RequestId;
      }

      const successMessage = commandObj.successMessage;

      delete commandObj.successMessage;
      const commandObjJSON = JSON.stringify(commandObj);
      registerCommand(
        componentRid as string,
        componentCallback,
        componentCallbackUpdate,
        componentCallbackError,
        commandObj,
        successMessage
      );

      const isConnected = Store.getState().socket.isJackpotConnected;

      if (commandObjJSON && (isConnected || reConnect)) {
        socket.send(commandObjJSON);
      }
    };

    const unsubscribe = (commandRid: string) => {
      const command = {
        command: CommandNames.UNSUBSCRIBE,
        Data: {
          PartnerId: +partnerId
        },
        RequestId: commandRid
      };

      socket.send(JSON.stringify(command));
      unregisterCommand(commandRid);
    };

    const unsubscribeAll = (commandRids: Array<string>) => {
      commandRids.forEach((item: string) => {
        const command = {
          command: CommandNames.UNSUBSCRIBE,
          Data: {
            PartnerId: +partnerId
          },
          RequestId: item
        };

        unregisterCommand(item);

        socket.send(JSON.stringify(command));
      });
    };

    const socketReconnect = () => {
      socket.reconnect();
    };

    if (socket) {
      socket.addEventListener('open', _socketOpen);
      socket.addEventListener('close', _socketClose);
      socket.addEventListener('message', _socketMessage);
      socket.addEventListener('error', _socketError);
      socket.sendCommand = sendCommand;
      socket.unsubscribe = unsubscribe;
      socket.unsubscribeAll = unsubscribeAll;
      socket.socketReconnect = socketReconnect;

      window.addEventListener('reconnect', socketReconnect, false);
    }
  });
};
